import { useState } from 'react'
import { generateGuiExercise } from '../../tool/guiExerciseChat'
import { showToast } from '../commonUnit/Toast'

/**
 * Natural-language exercise authoring, embedded in GuiExerciseEditor's edit
 * mode. The model only ever proposes assignment/starter-code text back into
 * the parent's (unsaved) edit buffers via onApply - it never writes to the
 * session directly. The instructor still has to review the populated fields
 * and press Save, same as every other edit made through this form.
 */
export default function GuiExerciseAiChat({ enabledFiles, current, onApply }) {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSend = async () => {
    const text = input.trim()
    if (!text || isLoading) return

    const nextHistory = [...messages, { role: 'user', content: text }]
    setMessages(nextHistory)
    setInput('')
    setIsLoading(true)

    try {
      const result = await generateGuiExercise(nextHistory, current, enabledFiles)
      setMessages((prev) => [...prev, { role: 'assistant', content: result.reply || 'Done - I updated the fields above.' }])
      onApply(result)
    } catch (error) {
      console.error('GUI exercise chat failed:', error)
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, I could not generate that. Please try rephrasing.' }])
      showToast('AI Assistant request failed.', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="gui-exercise-ai">
      <button type="button" className="gui-exercise-btn" onClick={() => setOpen((prev) => !prev)}>
        {open ? 'Hide AI Assistant' : 'AI Assistant'}
      </button>

      {open && (
        <div className="gui-exercise-ai-panel">
          <p className="gui-exercise-note">
            Describe the exercise you want in plain language. Each reply fills in the assignment and starter
            code fields above for you to review - nothing is saved until you press Save.
          </p>
          <div className="gui-exercise-ai-messages">
            {messages.length === 0 && (
              <div className="gui-exercise-ai-placeholder">
                e.g. "Make this a form validation exercise instead - the student should show an error message
                if the email field is empty when submitted."
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`gui-exercise-ai-message gui-exercise-ai-message-${m.role}`}>
                {m.content}
              </div>
            ))}
            {isLoading && <div className="gui-exercise-ai-message gui-exercise-ai-message-assistant">Thinking…</div>}
          </div>
          <div className="gui-exercise-ai-input-row">
            <textarea
              className="gui-exercise-ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder="Describe the exercise..."
              disabled={isLoading}
            />
            <button
              type="button"
              className="gui-exercise-btn gui-exercise-btn-primary"
              onClick={handleSend}
              disabled={isLoading || !input.trim()}>
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
